import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Table, Button, Tooltip } from "antd";
import NameAvatar from "./NameAvatar";
import { useTranslation } from "react-i18next";
import { postRequest } from "../../services";
import { toast } from "react-toastify"; 

const AttendanceTable = forwardRef((props, ref) => {
  const { myAttendaceData, classData, fetchAttendanceData, readOnly = false } = props;

  const [transformedData, setTransformedData] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "sa";

  // Initialize attendance options with tooltips
  const attendanceOptions = [
    { label: "PP", checked: false, tooltip: "Present Physically" }, 
    { label: "PO", checked: false, tooltip: "Present Online" }, 
    { label: "A", checked: false, tooltip: "Absent" },
    { label: "L", checked: false, tooltip: "Late" },
  ];

  useEffect(() => {
    // Transform the incoming student data for table display
    if (myAttendaceData?.students && classData) {
      const newData = myAttendaceData.students.map((student) => {
        // Deep clone the attendance options to avoid reference issues
        const attendance = attendanceOptions.map(opt => ({...opt}));
        
        // If we have last attendance data, we should pre-select the right status
        if (myAttendaceData.lastAttendance && student.lastAttendanceStatus) {
          // Find and check the matching attendance option
          const statusIndex = attendance.findIndex(opt => opt.label === student.lastAttendanceStatus);
          if (statusIndex !== -1) {
            attendance[statusIndex].checked = true;
          }
        }
        
        return {
          key: student.id,
          id: student.id,
          name: student.name,
          dp: student.profilePicture,
          section: classData.sectionName || "Unknown",
          attendance: attendance,
          lastAttendanceStatus: student.lastAttendanceStatus || null
        };
      });
      
      setTransformedData(newData);
      
      // Initialize attendance data from any existing values
      const initialAttendanceData = newData
        .filter(student => {
          // Find the checked status for this student
          const checkedStatus = student.attendance.find(att => att.checked)?.label;
          return checkedStatus ? true : false;
        })
        .map(student => ({
          studentId: student.id,
          status: student.attendance.find(att => att.checked)?.label || ""
        }));
        
      setAttendanceData(initialAttendanceData);
    }
  }, [myAttendaceData, classData]);

  const handleAttBtnClick = (index, studentId) => {
    if (readOnly) return; // Don't allow changes if in read-only mode
    
    // Update the attendance status for the selected student
    const updatedData = transformedData.map((student) => {
      if (student.id === studentId) {
        // Reset all attendance options and set the selected one to checked
        const updatedAttendance = student.attendance.map((att, i) => ({
          ...att,
          checked: i === index,
        }));

        // Return updated student record
        return {
          ...student,
          attendance: updatedAttendance
        };
      }
      return student;
    });

    // Update attendance data for API submission
    const selectedStudent = updatedData.find(student => student.id === studentId);
    const selectedStatus = selectedStudent.attendance.find(att => att.checked)?.label || "";
    
    // Update or add attendance record for this student
    const updatedAttendanceData = attendanceData.filter(data => data.studentId !== studentId);
    updatedAttendanceData.push({
      studentId: studentId,
      status: selectedStatus
    });

    setAttendanceData(updatedAttendanceData);
    setTransformedData(updatedData);
  };

  const createAttendanceButtons = (attendance, studentId) => {
    return attendance.map((item, index) => (
      <Tooltip key={index} title={item.tooltip}>
        <Button
          type={item.checked ? "primary" : "default"}
          onClick={() => handleAttBtnClick(index, studentId)}
          style={{ 
            backgroundColor: item.checked ? "#463C77" : "",
            borderColor: item.checked ? "#463C77" : "",
            margin: '0 2px',
            cursor: readOnly ? "not-allowed" : "pointer",
            opacity: readOnly ? 0.7 : 1
          }}
          disabled={readOnly}
        >
          {item.label}
        </Button>
      </Tooltip>
    ));
  };

  const triggerSaveAttendance = async () => {
    if (readOnly) {
      toast.info("Attendance for this section has already been marked today.");
      return false;
    }

    // Make sure we have at least one student's attendance marked
    if (attendanceData.length === 0) {
      toast.warning("Please mark attendance for at least one student before saving.");
      return false;
    }

    // Check if every student has an attendance record
    if (attendanceData.length < transformedData.length) {
      // Get list of students without attendance
      const unmarkedStudents = transformedData.filter(
        student => !attendanceData.some(data => data.studentId === student.id)
      );
      
      if (unmarkedStudents.length > 0) {
        const confirmSave = window.confirm(
          `${unmarkedStudents.length} student(s) don't have attendance marked. Do you want to continue saving?`
        );
        
        if (!confirmSave) {
          return false;
        }
      }
    }
    
    setSubmitting(true);
    
    try {
      // Get the section ID from the class data
      const sectionId = classData.sectionId;
      
      if (!sectionId) {
        console.error("Section ID is missing");
        toast.error("Section ID is missing. Cannot save attendance.");
        return false;
      }
      
      const date = new Date();
      const formattedDate = date.toISOString();
      
      const payload = {
        sectionId: sectionId,
        date: formattedDate,
        students: attendanceData
      };
      
      console.log("Submitting attendance payload:", payload);
      
      const response = await postRequest(`/api/Attendance/mark-all-by-section`, payload);
      
      if (response && response.data && response.data.success) {
        toast.success(response.data.message || "Attendance saved successfully");
        fetchAttendanceData(); // Refresh data after saving
        return true;
      } else {
        const errorMsg = response?.data?.error || response?.data?.message || "Failed to save attendance";
        toast.error(errorMsg);
        return false;
      }
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast.error("An error occurred while saving the attendance");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  useImperativeHandle(ref, () => ({
    triggerSaveAttendance,
  }));

  const columns = [
    {
      title: t("studentName"),
      dataIndex: "name",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          {record.dp ? 
            <img 
              src={record.dp} 
              alt="user" 
              style={{ borderRadius: '50%', width: '30px', height: '30px', objectFit: 'cover' }} 
            /> 
            : 
            <NameAvatar name={text} rounded />
          }
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: t("Section"),
      dataIndex: "section",
    },
    {
      title: readOnly ? t("attendanceStatus") : t("markAttendance"),
      dataIndex: "attendance",
      render: (attendance, record) => (
        <div className="d-flex gap-2">
          {createAttendanceButtons(attendance, record.id)}
        </div>
      ),
    }
  ];

  return (
    <div>
      {readOnly && (
        <div className="mb-3">
          <em className="text-muted">This attendance record is read-only as it has already been marked for today.</em>
        </div>
      )}
      <div className="mb-3">
        <div className="d-flex gap-3 flex-wrap">
          <span><strong>PP</strong>: Present Physically</span>
          <span><strong>PO</strong>: Present Online</span>
          <span><strong>A</strong>: Absent</span>
          <span><strong>L</strong>: Late</span>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={transformedData}
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
        rowClassName={(record, index) => (index % 2 === 0 ? 'even-row' : 'odd-row')}
        dir={isArabic ? "rtl" : "ltr"}
        locale={{ emptyText: "No students found" }}
        loading={submitting}
      />
    </div>
  );
});

export default AttendanceTable;